import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import useAuth from '../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { OlCommentInput, OlInput } from '../../../../lib/types/models/ol/ol-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import OlCommentForm from './ol-comment-form'

const OlCommentsOlForm = () => {
  const form = useFormContext<OlInput>()
  const comments = form.watch('comments') || []
  const { user } = useAuth()

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit',
    OlCommentInput
  >(['create', 'edit'])

  const columns: Column<IdInput<OlCommentInput>>[] = [
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography variant="tag" className="whitespace-pre-wrap break-all ">
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedByName'
    },
    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'date',
      cell: ({ row }) => (
        <Badge text={DateFormatter.formatDateHour(row.original.date)} variant="grey" />
      )
    },
    {
      enableSorting: false,
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />

          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() =>
              form.setValue(
                'comments',
                comments.filter((_, index) => index !== row.original.id)
              )
            }
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'Commentaires'}
      actions={[
        <Button
          key={'button'}
          type="button"
          icon={PlusIcon}
          label={'Ajouter un commentaire'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      <DataTable<IdInput<OlCommentInput>>
        manualSorting={false}
        display={comments.length > 0}
        columns={columns}
        data={comments.map((comment, index) => ({ ...comment, id: index }))}
        rowCount={comments.length}
        paginated={false}
      />

      <Modal
        isOpen={modalState.create}
        title={'Écrire un commentaire'}
        icon={MessageIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <OlCommentForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            date: new Date(),
            commentedByName: toFullName(user.firstName, user.lastName)
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create'),
            onConfirm: (data) => {
              form.setValue('comments', [...comments, data])
              toggleModal('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Éditer un commentaire'}
          icon={MessageIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <OlCommentForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => toggleModal('edit'),
              onConfirm: (data) => {
                form.setValue(
                  'comments',
                  comments.map((comment, index) => (index === selectedData.id ? data : comment))
                )
                toggleModal('edit')
              }
            })}
          />
        </Modal>
      )}
    </ActionTableWrapper>
  )
}

export default OlCommentsOlForm
